import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { IconButton } from './IconButton';

export interface SheetProps {
    isOpen: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    /** Shown right of the title (e.g. an amount) */
    titleAside?: React.ReactNode;
    /** Replaces the title row entirely */
    header?: React.ReactNode;
    children: React.ReactNode;
    /** Dialog max-width on lg: sm 480 · md 560 · lg 600 · xl 760 · full */
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    showCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    footer?: React.ReactNode;
    /** Remove the body padding/scroll (for custom layouts) */
    bare?: boolean;
    className?: string;
}

const widths = { sm: 'lg:max-w-[480px]', md: 'lg:max-w-[560px]', lg: 'lg:max-w-[600px]', xl: 'lg:max-w-[760px]', full: 'lg:max-w-[calc(100vw-96px)]' };

/**
 * T4 — bottom sheet on phones, centred 32px dialog on lg. Plum scrim.
 * Also exported as `Modal` so legacy call sites keep compiling.
 */
export const Sheet: React.FC<SheetProps> = ({ isOpen, onClose, title, titleAside, header, children, size = 'md', showCloseButton = true, closeOnOverlayClick = true, footer, bare = false, className }) => {
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[900] flex items-end justify-center bg-[#3a2634]/70 animate-fade-in lg:items-center lg:p-6"
            onClick={closeOnOverlayClick ? onClose : undefined}
        >
            <div
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
                className={clsx(
                    'flex w-full max-h-[88dvh] flex-col bg-surface-card shadow-xl',
                    'rounded-sheet px-5 pb-[max(26px,env(safe-area-inset-bottom))] pt-3 animate-slide-up',
                    'lg:max-h-[85vh] lg:rounded-[32px] lg:p-8 lg:animate-pop-in',
                    widths[size],
                    className,
                )}
            >
                <div className="mx-auto mb-3 h-[5px] w-11 shrink-0 rounded-pill bg-line lg:hidden" />
                {header ?? ((title || showCloseButton) && (
                    <div className="mb-3 flex shrink-0 items-center gap-2.5">
                        {title && <h2 className="min-w-0 flex-1 truncate text-[18px] lg:text-[22px] font-black leading-tight">{title}</h2>}
                        {titleAside}
                        {showCloseButton && <IconButton variant="sunken" size="xs" label="Close" onClick={onClose}><X /></IconButton>}
                    </div>
                ))}
                <div className={clsx('min-h-0 flex-1', !bare && 'overflow-y-auto')}>{children}</div>
                {footer && <div className="mt-3 shrink-0">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
};

export default Sheet;
